import React, { useEffect, useRef, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Loader2, Plus, Trash2, Upload, Wand2 } from "lucide-react";
import { emptyRoll, fmtQty, GRADES, type ReceiptLineDraft, type RollDraft, type WarehouseRecord } from "@/types/inventory";
import { importStockCsv, newLineKey, STOCK_CSV_SAMPLE } from "@/lib/stockImport";

const field = "w-full h-9 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";
const cell = "h-8 w-full rounded-md border border-input bg-background px-2 text-xs focus:outline-hidden focus:ring-2 focus:ring-ring";
const today = () => new Date().toISOString().slice(0, 10);

const blankLine = (): ReceiptLineDraft => ({ key: newLineKey(), product_id: "", product_label: "", variant_id: "", lot_number: "", unit: "", variants: [], rolls: [emptyRoll()] });

interface Props {
  open: boolean;
  /** PURCHASE for material bought from a supplier; OPENING for stock already on the shelf */
  mode: "PURCHASE" | "OPENING";
  onClose: () => void;
  onSaved: () => void;
}

/** Receive material into a warehouse roll by roll (a purchase, or opening stock typed or imported from a CSV) */
export function ReceiveDialog({ open, mode, onClose, onSaved }: Props) {
  const { success, error } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [head, setHead] = useState({ supplier_id: "", supplier_label: "", warehouse_id: "", receipt_date: today(), supplier_invoice_no: "", supplier_challan_no: "", transporter: "", vehicle_number: "", notes: "" });
  const [lines, setLines] = useState<ReceiptLineDraft[]>([blankLine()]);
  const [gen, setGen] = useState<Record<string, { count: string; qty: string; prefix: string; start: string }>>({});

  useEffect(() => {
    if (!open) return;
    setProblems([]);
    setLines([blankLine()]);
    setGen({});
    api
      .get("/api/Warehouse/List")
      .then((r) => {
        const list = unwrapArray<WarehouseRecord>(r);
        setWarehouses(list);
        setHead({ supplier_id: "", supplier_label: "", warehouse_id: list.find((w) => w.is_default)?.id || list[0]?.id || "", receipt_date: today(), supplier_invoice_no: "", supplier_challan_no: "", transporter: "", vehicle_number: "", notes: "" });
      })
      .catch(() => setWarehouses([]));
  }, [open, mode]);

  const patchLine = (key: string, patch: Partial<ReceiptLineDraft>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const patchRoll = (key: string, idx: number, patch: Partial<RollDraft>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, rolls: l.rolls.map((r, i) => (i === idx ? { ...r, ...patch } : r)) } : l)));

  const pickProduct = async (key: string, id: string, row: any) => {
    patchLine(key, { product_id: id, product_label: row ? `${row.name} (${row.code})` : "", unit: row?.unit || "", variant_id: "", variants: [] });
    if (!id) return;
    try {
      const v = unwrapArray<any>(await api.get(`/api/Product/Variants/${id}`));
      patchLine(key, { variants: v.map((x) => ({ id: x.id, name: x.name, code: x.code })) });
    } catch {
      /* the product simply has no shades to choose */
    }
  };

  const generate = (key: string) => {
    const g = gen[key];
    const count = Math.floor(Number(g?.count));
    const qty = Number(g?.qty);
    if (!(count > 0 && count <= 500) || !(qty > 0)) return error("Validation Error", "Enter how many rolls (up to 500) and the quantity of each");
    const start = Math.floor(Number(g?.start)) || 1;
    const prefix = g?.prefix || "";
    const width = String(start + count - 1).length;
    const rolls = Array.from({ length: count }, (_, i) => ({ ...emptyRoll(), unit_no: `${prefix}${String(start + i).padStart(width, "0")}`, quantity: String(qty) }));
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, rolls: [...l.rolls.filter((r) => r.unit_no || r.quantity), ...rolls] } : l)));
  };

  /** "R001, 100, 22.5, 23" per line (comma or tab separated), straight from a spreadsheet */
  const paste = (key: string, text: string) => {
    const rolls: RollDraft[] = text
      .split(/\r?\n/)
      .map((ln) => ln.split(/[,\t]/).map((c) => c.trim()))
      .filter((c) => c[0] && Number(c[1]) > 0)
      .map((c) => ({ ...emptyRoll(), unit_no: c[0]!, quantity: c[1]!, net_weight: c[2] || "", gross_weight: c[3] || "" }));
    if (!rolls.length) return error("Nothing to add", "Put one roll per line: roll number, quantity, then optionally net and gross weight");
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, rolls: [...l.rolls.filter((r) => r.unit_no || r.quantity), ...rolls] } : l)));
  };

  const onFile = async (file?: File | null) => {
    if (!file) return;
    setImporting(true);
    try {
      const res = await importStockCsv(await file.text());
      setProblems(res.problems);
      if (res.lines.length) {
        setLines((ls) => [...ls.filter((l) => l.product_id || l.rolls.some((r) => r.unit_no)), ...res.lines]);
        success("Imported", `${res.lines.reduce((n, l) => n + l.rolls.length, 0)} rolls read from the file${res.problems.length ? `; ${res.problems.length} row(s) skipped` : ""}`);
      } else {
        error("Nothing imported", res.problems[0] || "No rows could be read");
      }
    } catch (err: any) {
      error("Import failed", err.message || "The file could not be read");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadSample = () => {
    const url = URL.createObjectURL(new Blob([STOCK_CSV_SAMPLE], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "opening-stock-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "PURCHASE" && !head.supplier_id) return error("Validation Error", "Choose the supplier");
    if (!head.warehouse_id) return error("Validation Error", "Choose the warehouse");
    const used = lines.filter((l) => l.product_id || l.rolls.some((r) => r.unit_no || r.quantity));
    if (!used.length) return error("Validation Error", "Add at least one product with its rolls");
    for (const l of used) {
      if (!l.product_id) return error("Validation Error", "Choose the product on every line");
      const rolls = l.rolls.filter((r) => r.unit_no || r.quantity);
      if (!rolls.length) return error("Validation Error", `${l.product_label}: add at least one roll`);
      for (const r of rolls) {
        if (!r.unit_no.trim()) return error("Validation Error", `${l.product_label}: every roll needs a number`);
        if (!(Number(r.quantity) > 0)) return error("Validation Error", `${l.product_label}, roll ${r.unit_no}: enter a quantity above zero`);
      }
    }
    setSaving(true);
    try {
      await api.post("/api/GoodsReceipt/Insert", {
        receipt_type: mode,
        supplier_id: mode === "PURCHASE" ? head.supplier_id : undefined,
        warehouse_id: head.warehouse_id,
        receipt_date: head.receipt_date || undefined,
        supplier_invoice_no: head.supplier_invoice_no || undefined,
        supplier_challan_no: head.supplier_challan_no || undefined,
        transporter: head.transporter || undefined,
        vehicle_number: head.vehicle_number || undefined,
        notes: head.notes || undefined,
        lines: used.map((l) => ({
          product_id: l.product_id,
          variant_id: l.variant_id || undefined,
          lot_number: l.lot_number.trim() || undefined,
          unit: l.unit.trim() || undefined,
          rolls: l.rolls
            .filter((r) => r.unit_no || r.quantity)
            .map((r) => ({
              unit_no: r.unit_no.trim(),
              quantity: Number(r.quantity),
              net_weight: r.net_weight === "" ? undefined : Number(r.net_weight),
              gross_weight: r.gross_weight === "" ? undefined : Number(r.gross_weight),
              grade: r.grade || undefined,
              location: r.location.trim() || undefined,
            })),
        })),
      });
      success(mode === "OPENING" ? "Opening stock saved" : "Goods received", "The rolls are now in stock");
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  const total = (l: ReceiptLineDraft) => l.rolls.reduce((s, r) => s + (Number(r.quantity) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-4xl md:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{mode === "OPENING" ? "Opening stock" : "Receive goods"}</DialogTitle>
          <DialogDescription>
            {mode === "OPENING"
              ? "Enter the stock already on your shelves, roll by roll, or import it from a CSV file."
              : "Book material from a supplier into a warehouse. Each roll, bale or bag is recorded with its lot."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col min-h-0">
          <div className="space-y-4 py-2 text-sm overflow-y-auto max-h-[68vh] pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {mode === "PURCHASE" && (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Supplier *</Label>
                  <SearchSelect
                    endpoint="/api/Supplier/List"
                    value={head.supplier_id}
                    selectedLabel={head.supplier_label}
                    getLabel={(row) => `${row.name}${row.city ? ` · ${row.city}` : ""}`}
                    placeholder="Search suppliers"
                    onChange={(id, row) => setHead({ ...head, supplier_id: id, supplier_label: row?.name || "" })}
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Warehouse *</Label>
                <select className={field} value={head.warehouse_id} onChange={(e) => setHead({ ...head, warehouse_id: e.target.value })}>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>{mode === "OPENING" ? "As on date" : "Received on"}</Label>
                <Input type="date" max={today()} value={head.receipt_date} onChange={(e) => setHead({ ...head, receipt_date: e.target.value })} className="h-9" />
              </div>
              {mode === "PURCHASE" && (
                <>
                  <div className="space-y-1.5">
                    <Label>Supplier invoice no.</Label>
                    <Input value={head.supplier_invoice_no} onChange={(e) => setHead({ ...head, supplier_invoice_no: e.target.value })} className="h-9" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Challan no.</Label>
                    <Input value={head.supplier_challan_no} onChange={(e) => setHead({ ...head, supplier_challan_no: e.target.value })} className="h-9" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Transporter / vehicle</Label>
                    <div className="flex gap-2">
                      <Input placeholder="Transporter" value={head.transporter} onChange={(e) => setHead({ ...head, transporter: e.target.value })} className="h-9" />
                      <Input placeholder="Vehicle no." value={head.vehicle_number} onChange={(e) => setHead({ ...head, vehicle_number: e.target.value })} className="h-9" />
                    </div>
                  </div>
                </>
              )}
            </div>

            {mode === "OPENING" && (
              <div className="rounded-lg border border-dashed border-border p-3 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground">One row per roll: product_code, variant_code, lot_number, roll_no, quantity, net_weight, gross_weight, grade, location.</span>
                <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5" onClick={downloadSample}>
                  <Download className="size-3.5" /> Sample file
                </Button>
                <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5" disabled={importing} onClick={() => fileRef.current?.click()}>
                  {importing ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />} Import CSV
                </Button>
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              </div>
            )}
            {problems.length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-0.5 max-h-28 overflow-y-auto">
                <p className="font-semibold">Some rows were skipped:</p>
                {problems.slice(0, 30).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
                {problems.length > 30 && <p>…and {problems.length - 30} more</p>}
              </div>
            )}

            {lines.map((l, li) => (
              <div key={l.key} className="rounded-xl border border-border bg-muted/20 p-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                  <div className="space-y-1 sm:col-span-5">
                    <Label className="text-xs">Product *</Label>
                    <SearchSelect
                      endpoint="/api/Product/List"
                      extraParams={{ active: "true" }}
                      value={l.product_id}
                      selectedLabel={l.product_label}
                      getLabel={(row) => `${row.name} (${row.code})`}
                      placeholder="Search products"
                      onChange={(id, row) => pickProduct(l.key, id, row)}
                    />
                  </div>
                  <div className="space-y-1 sm:col-span-3">
                    <Label className="text-xs">Shade</Label>
                    <select className={field} value={l.variant_id} onChange={(e) => patchLine(l.key, { variant_id: e.target.value })} disabled={!l.variants.length}>
                      <option value="">{l.variants.length ? "-- No shade --" : "No shades"}</option>
                      {l.variants.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs">Dye lot / batch</Label>
                    <Input value={l.lot_number} onChange={(e) => patchLine(l.key, { lot_number: e.target.value })} placeholder="Receipt no." className="h-9" />
                  </div>
                  <div className="space-y-1 sm:col-span-1">
                    <Label className="text-xs">Unit</Label>
                    <Input value={l.unit} onChange={(e) => patchLine(l.key, { unit: e.target.value.toUpperCase() })} className="h-9" />
                  </div>
                  <div className="sm:col-span-1 flex justify-end">
                    {lines.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" className="size-9 text-destructive" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} title="Remove this line">
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-end gap-2 text-xs">
                  <span className="font-semibold self-center">Make rolls:</span>
                  <Input placeholder="How many" className="h-8 w-24 text-xs" inputMode="numeric" value={gen[l.key]?.count || ""} onChange={(e) => setGen({ ...gen, [l.key]: { ...(gen[l.key] || { count: "", qty: "", prefix: "", start: "" }), count: e.target.value } })} />
                  <Input placeholder="Qty each" className="h-8 w-24 text-xs" inputMode="decimal" value={gen[l.key]?.qty || ""} onChange={(e) => setGen({ ...gen, [l.key]: { ...(gen[l.key] || { count: "", qty: "", prefix: "", start: "" }), qty: e.target.value } })} />
                  <Input placeholder="Number prefix" className="h-8 w-28 text-xs" value={gen[l.key]?.prefix || ""} onChange={(e) => setGen({ ...gen, [l.key]: { ...(gen[l.key] || { count: "", qty: "", prefix: "", start: "" }), prefix: e.target.value } })} />
                  <Input placeholder="Start at" className="h-8 w-20 text-xs" inputMode="numeric" value={gen[l.key]?.start || ""} onChange={(e) => setGen({ ...gen, [l.key]: { ...(gen[l.key] || { count: "", qty: "", prefix: "", start: "" }), start: e.target.value } })} />
                  <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => generate(l.key)}>
                    <Wand2 className="size-3.5" /> Add
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8"
                    onClick={async () => {
                      try {
                        paste(l.key, await navigator.clipboard.readText());
                      } catch {
                        error("Can't read the clipboard", "Allow clipboard access, or type the rolls in the table");
                      }
                    }}
                    title="Roll number, quantity, net and gross weight per line, copied from a spreadsheet"
                  >
                    Paste from spreadsheet
                  </Button>
                </div>

                <div className="rounded-lg border border-border bg-card overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border">
                        <th className="px-2 py-1.5 w-8">#</th>
                        <th className="px-2 py-1.5">Roll no. *</th>
                        <th className="px-2 py-1.5">Quantity *</th>
                        <th className="px-2 py-1.5">Net wt</th>
                        <th className="px-2 py-1.5">Gross wt</th>
                        <th className="px-2 py-1.5">Grade</th>
                        <th className="px-2 py-1.5">Rack</th>
                        <th className="w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      {l.rolls.map((r, ri) => (
                        <tr key={ri} className="border-b border-border/60 last:border-0">
                          <td className="px-2 py-1 text-muted-foreground">{ri + 1}</td>
                          <td className="px-1 py-1"><input className={cell} value={r.unit_no} onChange={(e) => patchRoll(l.key, ri, { unit_no: e.target.value })} /></td>
                          <td className="px-1 py-1"><input className={cell} inputMode="decimal" value={r.quantity} onChange={(e) => patchRoll(l.key, ri, { quantity: e.target.value })} /></td>
                          <td className="px-1 py-1"><input className={cell} inputMode="decimal" value={r.net_weight} onChange={(e) => patchRoll(l.key, ri, { net_weight: e.target.value })} /></td>
                          <td className="px-1 py-1"><input className={cell} inputMode="decimal" value={r.gross_weight} onChange={(e) => patchRoll(l.key, ri, { gross_weight: e.target.value })} /></td>
                          <td className="px-1 py-1">
                            <select className={cell} value={r.grade} onChange={(e) => patchRoll(l.key, ri, { grade: e.target.value })}>
                              {Object.entries(GRADES).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-1 py-1"><input className={cell} value={r.location} onChange={(e) => patchRoll(l.key, ri, { location: e.target.value })} /></td>
                          <td className="px-1 py-1">
                            {l.rolls.length > 1 && (
                              <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => patchLine(l.key, { rolls: l.rolls.filter((_, i) => i !== ri) })} title="Remove roll">
                                <Trash2 className="size-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <Button type="button" size="sm" variant="ghost" className="h-7 gap-1" onClick={() => patchLine(l.key, { rolls: [...l.rolls, emptyRoll()] })}>
                    <Plus className="size-3.5" /> Add a roll
                  </Button>
                  <span className="text-muted-foreground">
                    Line {li + 1}: <b className="text-foreground">{l.rolls.filter((r) => r.unit_no || r.quantity).length}</b> rolls · <b className="text-foreground">{fmtQty(total(l), l.unit)}</b>
                  </span>
                </div>
              </div>
            ))}

            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setLines((ls) => [...ls, blankLine()])}>
              <Plus className="size-4" /> Add another product / lot
            </Button>

            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea rows={2} value={head.notes} onChange={(e) => setHead({ ...head, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-1.5">
              {saving && <Loader2 className="size-4 animate-spin" />}
              {mode === "OPENING" ? "Save opening stock" : "Receive into stock"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
